import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Remembers the URL each list screen was last left at, so a detail/edit page's
 * "back to the list" lands on the list the operator actually had - same page
 * size, page, search, filters, sort - instead of a bare path that silently
 * resets every one of them.
 *
 * Why a side channel and not `navigate(-1)`: an edit page is also reached by
 * deep link, by a bookmark, from the command palette, and right after Create
 * (where the previous history entry is the create form, not the list). A
 * recorded URL degrades to the bare base path in all of those, which is the
 * right landing spot; `navigate(-1)` would leave the app or bounce back into
 * a form.
 *
 * Why sessionStorage and not a module-level Map: it survives a reload of the
 * detail page, and it is per-tab, so two tabs can sit on two different pages
 * of the same list without fighting.
 *
 * Recording lives in `useDataGridState` - the single hook every list screen's
 * URL state already flows through - so a list opts in by doing nothing.
 */

const STORAGE_KEY = 'utazzo.admin.list-return';

/** Keeps the map bounded. Entries evict oldest-write-first. */
const MAX_ENTRIES = 24;

type ReturnMap = Record<string, string>;

const readMap = (): ReturnMap => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed !== null && typeof parsed === 'object' ? (parsed as ReturnMap) : {};
  } catch {
    return {};
  }
};

const writeMap = (map: ReturnMap): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Private mode / quota exceeded. Back still works, it just lands on the
    // bare path - the same behaviour as before this hook existed.
  }
};

/**
 * Record where a list screen currently stands.
 *
 * `search` is the live query string INCLUDING its leading '?', or '' when the
 * list sits at its defaults - in which case the entry is dropped, so arriving
 * from the sidebar really does start fresh rather than resurrecting a filter
 * set from ten minutes ago.
 *
 * Must be called with the query string that is already in the address bar:
 * nuqs throttles its `history.replaceState` writes, so callers reacting to a
 * grid change have to wait on the setter's promise before reading
 * `window.location.search`.
 */
export const rememberListLocation = (pathname: string, search: string): void => {
  if (typeof window === 'undefined') return;
  const map = readMap();
  // Delete first either way: re-inserting moves the key to the end, which is
  // what lets plain key order stand in for a write-recency order.
  delete map[pathname];
  if (search !== '' && search !== '?') map[pathname] = search;
  const keys = Object.keys(map);
  for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) delete map[stale];
  writeMap(map);
};

/** The remembered URL for `basePath`, or the bare path when nothing is on record. */
export const listReturnPath = (basePath: string): string => basePath + (readMap()[basePath] ?? '');

/** Test seam - drops every recorded location. */
export const clearListReturns = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // See writeMap.
  }
};

/**
 * A "back to the list" navigator for a detail / edit page. Pass the list's
 * route path exactly as the router declares it: `useListReturn('/activities')`.
 */
export const useListReturn = (basePath: string): (() => void) => {
  const navigate = useNavigate();
  return useCallback(() => {
    navigate(listReturnPath(basePath));
  }, [navigate, basePath]);
};
